import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useI18n } from '../../../i18n/useI18n';
import { toastMutationError } from '../../../query/mutationToasts';
import { toast } from '../../../toast/toast';
import type { KnowledgeBaseDocument } from '../../../types/knowledge';
import type {
  DocumentListOptions,
  WithResourceId,
} from '../../../types/knowledge';
import type { OperationProgress } from '../../../utils/operationProgress';
import {
  allSelectedSupport,
  canRetryFailedUploadDocument,
} from '../documentActionEligibility';
import {
  useDownloadKnowledgeDocumentMutation,
  useResumeAbandonedDocumentEmbeddingMutation,
} from '../mutations/useKnowledgeDocumentMutations';
import {
  mutateOnePromise,
  runBulkDownloadDocuments,
  runBulkResumeAbandonedDocuments,
} from './documentBulkMutationHelpers';
import { downloadRetryDocuments, retryDocuments } from './documentRetryUpload';
import type { BulkConfirmState } from './useDocumentSelection';
import { useDocumentBulkDeleteActions } from './useDocumentBulkDeleteActions';

type UseDocumentBulkActionsOptions = {
  databaseId: string;
  canManageDocuments: boolean;
  listOptions: DocumentListOptions;
  clearSelection: () => void;
  bulkConfirm: BulkConfirmState;
  setBulkConfirm: (value: BulkConfirmState) => void;
};

export function useDocumentBulkActions({
  databaseId,
  canManageDocuments,
  listOptions,
  clearSelection,
  bulkConfirm,
  setBulkConfirm,
}: UseDocumentBulkActionsOptions) {
  const { translate } = useI18n();
  const queryClient = useQueryClient();
  const downloadMutation = useDownloadKnowledgeDocumentMutation();
  const resumeAbandonedDocumentMutation =
    useResumeAbandonedDocumentEmbeddingMutation();
  const [isBulkActionRunning, setIsBulkActionRunning] = useState(false);
  const [retryPreparedFiles, setRetryPreparedFiles] = useState(
    () => new Map<string, File>(),
  );
  const [hasStartedRetryDownload, setHasStartedRetryDownload] = useState(false);
  const [isRetryDownloading, setIsRetryDownloading] = useState(false);
  const [isRetryConfirming, setIsRetryConfirming] = useState(false);
  const [bulkRetryDownloadProgress, setBulkRetryDownloadProgress] =
    useState<OperationProgress | null>(null);
  const [bulkRetryProgress, setBulkRetryProgress] =
    useState<OperationProgress | null>(null);
  const isRetryConfirmOpen = bulkConfirm?.type === 'retry';
  const [wasRetryConfirmOpen, setWasRetryConfirmOpen] =
    useState(isRetryConfirmOpen);

  if (wasRetryConfirmOpen !== isRetryConfirmOpen) {
    setWasRetryConfirmOpen(isRetryConfirmOpen);
    if (!isRetryConfirmOpen) {
      setRetryPreparedFiles(new Map());
      setHasStartedRetryDownload(false);
      setIsRetryDownloading(false);
      setIsRetryConfirming(false);
      setBulkRetryDownloadProgress(null);
      setBulkRetryProgress(null);
    }
  }

  const {
    pendingDelete,
    setPendingDelete,
    requestDeleteDocuments,
    confirmDeleteDocument,
    confirmBulkDeleteDocuments,
    isDeletingDocument,
    bulkDeleteProgress,
  } = useDocumentBulkDeleteActions({
    databaseId,
    canManageDocuments,
    listOptions,
    clearSelection,
    bulkConfirm,
    setBulkConfirm,
    isBulkActionRunning,
    setIsBulkActionRunning,
  });

  const handleDownload = (document: WithResourceId<KnowledgeBaseDocument>) => {
    downloadMutation.mutate(
      { databaseId, document },
      {
        onError: (error) => {
          void toastMutationError(
            error,
            translate('knowledge.downloadErrorTitle'),
            translate('knowledge.downloadErrorDescription'),
          );
        },
      },
    );
  };

  const handleDownloadDocuments = useCallback(
    (targets: Array<WithResourceId<KnowledgeBaseDocument>>) => {
      if (targets.length === 0 || isBulkActionRunning) {
        return;
      }

      void (async () => {
        setIsBulkActionRunning(true);
        await runBulkDownloadDocuments({
          targets,
          translate,
          mutateOne: (document) =>
            mutateOnePromise(
              downloadMutation,
              { databaseId, document },
              translate('knowledge.downloadErrorTitle'),
              translate('knowledge.downloadErrorDescription'),
            ),
        });
        setIsBulkActionRunning(false);
      })();
    },
    [databaseId, downloadMutation, isBulkActionRunning, translate],
  );

  const handleResumeAbandonedDocument = (
    document: WithResourceId<KnowledgeBaseDocument>,
  ) => {
    resumeAbandonedDocumentMutation.mutate(
      {
        databaseId,
        documentId: document.id,
        listOptions,
      },
      {
        onSuccess: () => {
          toast.success({
            title: translate('knowledge.documentResumeSuccessTitle'),
            description: translate(
              'knowledge.documentResumeSuccessDescription',
            ),
          });
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('knowledge.documentResumeError'),
          );
        },
      },
    );
  };

  const handleResumeAbandonedDocuments = useCallback(
    (targets: Array<WithResourceId<KnowledgeBaseDocument>>) => {
      if (targets.length === 0 || isBulkActionRunning) {
        return;
      }

      void (async () => {
        setIsBulkActionRunning(true);
        await runBulkResumeAbandonedDocuments({
          targets,
          translate,
          mutateOne: (document) =>
            mutateOnePromise(
              resumeAbandonedDocumentMutation,
              {
                databaseId,
                documentId: document.id,
                listOptions,
              },
              translate('knowledge.documentResumeError'),
            ),
        });
        setIsBulkActionRunning(false);
        clearSelection();
      })();
    },
    [
      clearSelection,
      databaseId,
      isBulkActionRunning,
      listOptions,
      resumeAbandonedDocumentMutation,
      translate,
    ],
  );

  const requestRetryDocuments = useCallback(
    (targets: Array<WithResourceId<KnowledgeBaseDocument>>) => {
      if (
        targets.length === 0 ||
        !allSelectedSupport(targets, canRetryFailedUploadDocument)
      ) {
        return;
      }
      setBulkConfirm({ type: 'retry', documents: targets });
    },
    [setBulkConfirm],
  );

  const downloadBulkRetryDocuments = useCallback(() => {
    if (
      bulkConfirm?.type !== 'retry' ||
      isBulkActionRunning ||
      isRetryDownloading ||
      isRetryConfirming
    ) {
      return;
    }

    const targets = bulkConfirm.documents;
    const pendingCount = targets.filter(
      (document) => !retryPreparedFiles.has(document.id),
    ).length;
    if (pendingCount === 0) {
      return;
    }

    void (async () => {
      setHasStartedRetryDownload(true);
      setIsRetryDownloading(true);
      setIsBulkActionRunning(true);
      setBulkRetryDownloadProgress({ completed: 0, total: pendingCount });

      try {
        const result = await downloadRetryDocuments({
          databaseId,
          targets,
          preparedFiles: retryPreparedFiles,
          translate,
          onProgress: setBulkRetryDownloadProgress,
        });
        setRetryPreparedFiles(result.preparedFiles);
      } finally {
        setBulkRetryDownloadProgress(null);
        setIsRetryDownloading(false);
        setIsBulkActionRunning(false);
      }
    })();
  }, [
    bulkConfirm,
    databaseId,
    isBulkActionRunning,
    isRetryConfirming,
    isRetryDownloading,
    retryPreparedFiles,
    translate,
  ]);

  const confirmBulkRetryDocuments = useCallback(() => {
    if (
      bulkConfirm?.type !== 'retry' ||
      isBulkActionRunning ||
      isRetryDownloading ||
      isRetryConfirming
    ) {
      return;
    }

    if (
      hasStartedRetryDownload &&
      retryPreparedFiles.size < bulkConfirm.documents.length
    ) {
      return;
    }

    const targets = bulkConfirm.documents;
    void (async () => {
      setIsRetryConfirming(true);
      setIsBulkActionRunning(true);
      setBulkRetryProgress({ completed: 0, total: targets.length });
      try {
        await retryDocuments({
          databaseId,
          targets,
          preparedFiles: retryPreparedFiles,
          queryClient,
          translate,
          clearSelection,
          setIsBulkActionRunning,
          setBulkConfirm,
          onProgress: setBulkRetryProgress,
        });
      } finally {
        setBulkRetryProgress(null);
        setIsRetryConfirming(false);
        setIsBulkActionRunning(false);
      }
    })();
  }, [
    bulkConfirm,
    clearSelection,
    databaseId,
    hasStartedRetryDownload,
    isBulkActionRunning,
    isRetryConfirming,
    isRetryDownloading,
    queryClient,
    retryPreparedFiles,
    setBulkConfirm,
    translate,
  ]);

  const allRetryFilesPrepared =
    bulkConfirm?.type === 'retry' &&
    retryPreparedFiles.size >= bulkConfirm.documents.length;

  const isRetryConfirmDisabled =
    isRetryDownloading || (hasStartedRetryDownload && !allRetryFilesPrepared);

  return {
    handleDownload,
    handleDownloadDocuments,
    handleResumeAbandonedDocument,
    handleResumeAbandonedDocuments,
    requestRetryDocuments,
    requestDeleteDocuments,
    pendingDelete,
    setPendingDelete,
    confirmDeleteDocument,
    isBulkActionRunning,
    confirmBulkDeleteDocuments,
    confirmBulkRetryDocuments,
    downloadBulkRetryDocuments,
    isDeletingDocument,
    bulkDeleteProgress,
    isRetryDownloading,
    isRetryConfirming,
    isRetryConfirmDisabled,
    bulkRetryDownloadProgress,
    bulkRetryProgress,
    hasStartedRetryDownload,
    allRetryFilesPrepared,
  };
}
